/**
 * Comment hooks — fetch comments, create comment, like comment.
 * Proper query key targeting to avoid cross-contamination.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchComments, createComment,
  likeComment, unlikeComment,
  fetchQuickComments,
} from '@turnifly/shared/api'
import type { CommentWithAuthor, QuickComment } from '@turnifly/shared/api'
import { feedKeys } from './use-feed'

// ── FETCH COMMENTS ─────────────────────────────────────────

export function useComments(postId: string | undefined, userId: string) {
  return useQuery<CommentWithAuthor[]>({
    queryKey: feedKeys.comments(postId ?? ''),
    queryFn: () => fetchComments(postId!, userId),
    enabled: !!postId && !!userId,
    staleTime: 30_000,
  })
}

// ── CREATE COMMENT ─────────────────────────────────────────

export function useCreateComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ postId, userId, text, parentId, isQuick }: {
      postId: string; userId: string; text: string
      parentId?: string | null; isQuick?: boolean
    }) => createComment(postId, userId, text, parentId, isQuick),
    onSuccess: (_data, variables) => {
      // Refresh comments for this post
      void queryClient.invalidateQueries({
        queryKey: feedKeys.comments(variables.postId),
      })
      // Refresh feed to update comments_count
      void queryClient.invalidateQueries({
        queryKey: ['feed', 'for-you'],
      })
      void queryClient.invalidateQueries({
        queryKey: ['feed', 'following'],
      })
    },
  })
}

// ── LIKE / UNLIKE COMMENT ──────────────────────────────────

export function useToggleCommentLike() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ commentId, userId, isLiked }: {
      commentId: string; userId: string; isLiked: boolean; postId: string
    }) => {
      if (isLiked) {
        await unlikeComment(commentId, userId)
      } else {
        await likeComment(commentId, userId)
      }
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: feedKeys.comments(variables.postId),
      })
    },
  })
}

// ── QUICK COMMENTS ─────────────────────────────────────────

export function useQuickComments() {
  return useQuery<QuickComment[]>({
    queryKey: feedKeys.quickComments,
    queryFn: fetchQuickComments,
    staleTime: Infinity,
  })
}
